import React, { useMemo } from 'react';
import { useGPS } from '../../contexts/GPSContext';
import { safeParseDate } from '../../utils/dateUtils';
import { buildUrlIssue } from '../../utils/url';
import SortableTable from '../SortableTable';
import StatTile from '../ui/StatTileNew';
import StatCard from '../ui/StatCard';
import { hasValidCost } from './gpsCostUtils';
import { isResolvedIssue } from '../../utils/gpsStatusCategories';

const INTERVENTION_DONE_STATUS = 'Intervention effectuée';

const formatDate = (value) => {
  const date = safeParseDate(value);
  if (!date) return '';

  return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const formatPercent = (value) => `${new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 1,
}).format(value)}%`;

const HealthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3h4v6h6v4h-6v8h-4v-8H4V9h6V3z" />
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const getHealthStats = (issues = []) => {
  const totalTickets = issues.length;
  const completedTickets = issues.filter(issue => isResolvedIssue(issue));
  const ticketsToValidate = issues.filter(issue => issue.status === INTERVENTION_DONE_STATUS);
  const completedTicketsWithoutCost = completedTickets.filter(issue => !hasValidCost(issue));
  const missingCostPercentage = completedTickets.length > 0 ? (completedTicketsWithoutCost.length / completedTickets.length) * 100 : 0;

  return {
    totalTickets,
    completedTickets,
    ticketsToValidate,
    completedTicketsWithoutCost,
    missingCostPercentage,
  };
};

const GPSSante = () => {
  const gps = useGPS();

  const stats = useMemo(() => getHealthStats(gps.issues || []), [gps.issues]);

  const issueColumns = useMemo(() => [
    {
      key: 'issue_key',
      label: 'Ticket',
      render: (row) => {
        const href = row.source === 'CSV' ? null : buildUrlIssue(row.project_key, row.issue_key);
        if (!href) return row.issue_key;

        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
            {row.issue_key}
          </a>
        );
      },
    },
    { key: 'status', label: 'Statut' },
    {
      key: 'status_date',
      label: 'Dernier statut',
      render: (row) => formatDate(row.status_date),
    },
    { key: 'request_type', label: 'Type' },
    { key: 'sector', label: 'Secteur' },
    { key: 'shopsign', label: 'Enseigne' },
    { key: 'intervention_point', label: 'Magasin' },
    {
      key: 'cost',
      label: 'Coût',
      render: (row) => hasValidCost(row) ? row.cost : (
        <span className="font-medium text-red-600 dark:text-red-400">Non renseigné</span>
      ),
    },
  ], []);

  return (
    <div className="space-y-6">
      <StatCard>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatTile
            label="Tickets à valider"
            value={stats.ticketsToValidate.length.toLocaleString('fr-FR')}
            icon={<HealthIcon />}
            color="orange"
            description="Tickets en Intervention effectuée, pas encore validés en Terminé(e)."
          />
          <StatTile
            label="Tickets sans coût"
            value={stats.completedTicketsWithoutCost.length.toLocaleString('fr-FR')}
            icon={<WarningIcon />}
            color="red"
            description="Tickets terminés dont le champ coût est vide ou invalide."
          />
          <StatTile
            label="Part sans coût"
            value={formatPercent(stats.missingCostPercentage)}
            icon={<WarningIcon />}
            color="red"
            description="Part des tickets terminés sans coût renseigné."
          />
        </div>
      </StatCard>

      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Tickets en attente de validation opérationnelle
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Tickets passés en Intervention effectuée mais pas encore en Terminé(e).
          </p>
        </div>
        <SortableTable
          data={stats.ticketsToValidate}
          columns={issueColumns}
          emptyMessage="Aucun ticket en attente de validation."
          exportable
          exportFilename="gps-tickets-a-valider.csv"
          initialSortBy={{ key: 'status_date', direction: 'descending' }}
        />
      </div>

      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Tickets sans coût renseigné
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {stats.completedTicketsWithoutCost.length.toLocaleString('fr-FR')} ticket{stats.completedTicketsWithoutCost.length > 1 ? 's' : ''} sans coût sur {stats.completedTickets.length.toLocaleString('fr-FR')} ticket{stats.completedTickets.length > 1 ? 's' : ''} terminé{stats.completedTickets.length > 1 ? 's' : ''}.
          </p>
        </div>
        <SortableTable
          data={stats.completedTicketsWithoutCost}
          columns={issueColumns}
          emptyMessage="Tous les tickets terminés ont un coût renseigné."
          exportable
          exportFilename="gps-tickets-sans-cout.csv"
          initialSortBy={{ key: 'status_date', direction: 'descending' }}
        />
      </div>
    </div>
  );
};

export default GPSSante;
